/**
 * Listening context panel component.
 *
 * Displays the current listening prompt/audio status and scratch notes in the
 * same left-panel pattern used by Writing's prompt surface.
 *
 * @module ListeningContextPanel
 */

import { useTranslation } from 'react-i18next';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { AudioWaveform } from '@/components/exam/AudioWaveform';
import { ReviewPeriodNotice } from '@/components/exam/ReviewPeriodNotice';
import {
  NotesPanel,
  SimulationPanelHeader,
} from '@/components/exam/shared';
import { cn } from '@/lib/utils';

interface ListeningContextPanelProps {
  readonly sectionTitle: string;
  readonly isReviewPeriod: boolean;
  readonly reviewMessage: string;
  readonly isAudioPlaying: boolean;
  readonly notes: string;
  readonly onNotesChange: (notes: string) => void;
  /** Real-audio driver for the waveform (fills array with 0–1 magnitudes). */
  readonly getAudioLevels?: (out: number[]) => boolean;
}

export function ListeningContextPanel({
  sectionTitle,
  isReviewPeriod,
  reviewMessage,
  isAudioPlaying,
  notes,
  onNotesChange,
  getAudioLevels,
}: ListeningContextPanelProps) {
  const { t } = useTranslation();

  const resizeAriaLabel = t('simulation.listening.context.resizeAriaLabel', {
    defaultValue: 'Resize audio context and notes',
  });

  return (
    <div className="h-full flex flex-col min-h-0 bg-card border-b lg:border-r lg:border-b-0 border-border">
      <SimulationPanelHeader
        title={
          <span className="uppercase tracking-tight text-sm sm:text-base">
            {sectionTitle}
          </span>
        }
        wrapTitle
      />

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <PanelGroup direction="vertical" className="flex h-full min-h-0 flex-1">
          <Panel defaultSize={34} minSize={22} className="min-h-0">
            <div className="flex h-full min-h-0 flex-col justify-center overflow-y-auto px-4 sm:px-5 py-4 bg-muted/10">
              {isReviewPeriod ? (
                <ReviewPeriodNotice message={reviewMessage} />
              ) : (
                <AudioWaveform
                  isPlaying={isAudioPlaying}
                  getLevels={getAudioLevels}
                  className="h-8 sm:h-10 gap-1"
                />
              )}
            </div>
          </Panel>

          <PanelResizeHandle
            className={cn(
              'group relative flex h-2.5 w-full shrink-0 items-center justify-center',
              'border-y border-border bg-muted/40 outline-none transition-colors',
              'hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'cursor-row-resize',
            )}
            aria-orientation="horizontal"
            aria-label={resizeAriaLabel}
          >
            <span className="pointer-events-none rounded-full bg-border/80 px-2 py-0.5 opacity-70 group-hover:opacity-100 inline-flex items-center">
              <span aria-hidden className="block w-4 h-0.5 rounded-full bg-muted-foreground/60" />
            </span>
          </PanelResizeHandle>

          <Panel defaultSize={66} minSize={28} className="min-h-0">
            <NotesPanel
              variant="compact"
              notes={notes}
              onNotesChange={onNotesChange}
              title={t('simulation.writing.prompt.notesLabel', { defaultValue: 'Notes' })}
              placeholder={t('simulation.notes.placeholder', {
                defaultValue: "Type quick notes here as you listen... These won't be graded.",
              })}
              showClear
            />
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}
